﻿import React, { useState, useEffect } from 'react';
import { OvenTemperatureProgram } from '../../models/oven-temperature-program';
import { ItemBasicInformation } from '../../models/item-basic-information';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';
import { CrudFunctions } from '../../utilities/crud-functions';
import { EditType } from '../../models/enums';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';

import DataGrid, {
    Column,
    Paging,
    LoadPanel,
    Selection,
    Toolbar,
    Editing,
    Button as ColumnButton,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';

import Guid from 'devextreme/core/guid';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export interface Props {
    benchSheetId?: string;
    onClose?: () => void;
}

export default function TemperatureProgramEditor(props: Props) {
    const [ovenTemperatureProgramEntries, setOvenTemperatureProgramEntries] =
        useState<OvenTemperatureProgram[]>();

    const [selectedOvenTemperatureProgramEntry, setSelectedOvenTemperatureProgramEntry] =
        useState<OvenTemperatureProgram>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getOvenTemperaturePrograms();
    }, []);

    key++;

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>
            </div>

            <div className="pw-margin-top-1">
                <DataGrid width="100%" height="100%"
                    wordWrapEnabled={true}
                    keyExpr="ovenTemperatureProgramId"
                    onSelectionChanged={onSelectionChanged}
                    dataSource={ovenTemperatureProgramEntries ?
                        ovenTemperatureProgramEntries : []}
                    onRowValidating={onOvenTemperatureProgramDataValidating}
                    onRowRemoving={onOvenTemperatureProgramRowRemoving}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    showBorders={true} >

                    <LoadPanel enabled={true} />
                    <Selection mode='single' />
                    <Paging enabled={false} />

                    <Column name="bc0" type="buttons" width='8rem'>
                        <ColumnButton hint="delete" icon="trash"
                            onClick={onOvenTemperatureProgramRowRemoving} />
                        <ColumnButton hint="certificate" icon="info"
                            onClick={onAnalysisDatumInfoButtonClick} />
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                    </Column>

                    <Column width='2rem' cssClass="table-vertical-align"
                        cellRender={editMarkerCellRender} />

                    <Column dataField='rowNumber' caption="" dataType="number"
                        allowEditing={false} width='2rem' />

                    <Column dataField='initTemperature' caption="Init Temp °C" dataType="number" width='10rem' />
                    <Column dataField='timeIncrement1' caption="Hold Time (min)" dataType="number" width='10rem' />
                    <Column dataField='secondTemperature' caption="Second Temp °C" dataType="number" width='10rem' />
                    <Column dataField='timeIncrement2' caption="Hold Time (min)" dataType="number" width='10rem' />
                    <Column dataField='rate' caption="Rate °C/min" dataType="number" width='10rem' />
                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Temperature Program</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <Button width='1.9rem' type='normal' icon="repeat"
                                hint="duplicate rows"
                                onClick={duplicateOvenTemperatureProgramRow} />
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <Button width='1.9rem' type='normal' icon="plus"
                                hint="add oven temperature program entry"
                                onClick={onAddOvenTemperatureProgramEntry} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing mode="row"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true} />

                </DataGrid>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog key={`ibi${key}`} initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }
        </div>
    )

    function onSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedOvenTemperatureProgramEntry(e.selectedRowsData[0]);
        }
    }

    function onSaveClick() {
        saveOvenTemperatureProgramData();
    }

    function onCloseClick() {
        const anyNonSavedChanges = ovenTemperatureProgramEntries.some(e => e.editType !== EditType.None);

        /* REWORK
        if (anyNonSavedChanges) {
            const modalContext: XXXContext = {
                modalType: ConfirmationModal,
                title: "Unsaved Items",
                message: "You have unsaved changes. Do you want to close anyway?",
                onModalConfirmed: closeDialog
            };

            XXX.publish(ModalSubscriberTopic, modalContext);
            return;
        }
        */

        closeDialog();
    }

    async function closeDialog() {
        if (props.onClose)
            props.onClose();
    }

    function onAddOvenTemperatureProgramEntry(e: any) {
        let newRowNumber = 1;

        if (ovenTemperatureProgramEntries)
            newRowNumber = ovenTemperatureProgramEntries.length + 1;

        let newOvenTemperatureProgramEntry: OvenTemperatureProgram = {
            ovenTemperatureProgramId: new Guid().toString(),
            rowNumber: newRowNumber,
            editType: EditType.Insert
        };

        let editedOvenTemperatureProgram = [...ovenTemperatureProgramEntries];

        editedOvenTemperatureProgram.push(newOvenTemperatureProgramEntry);

        editedOvenTemperatureProgram.sort((a, b) => a.rowNumber - b.rowNumber);

        setOvenTemperatureProgramEntries(editedOvenTemperatureProgram);
    }

    function duplicateOvenTemperatureProgramRow() {
        if (selectedOvenTemperatureProgramEntry) {
            let editedOvenTemperatureProgramData = [...ovenTemperatureProgramEntries];
            let newOvenTemperatureProgramEntry = { ...selectedOvenTemperatureProgramEntry };

            newOvenTemperatureProgramEntry.editType = EditType.Insert;
            newOvenTemperatureProgramEntry.ovenTemperatureProgramId = new Guid().toString();

            editedOvenTemperatureProgramData.push(newOvenTemperatureProgramEntry);

            editedOvenTemperatureProgramData.sort((a, b) => a.rowNumber - b.rowNumber);

            setOvenTemperatureProgramEntries(editedOvenTemperatureProgramData);
        }
    }

    function onOvenTemperatureProgramRowRemoving(e: any) {
        if (e.row && e.row.data && e.row.data.ovenTemperatureProgramId) {
            let editedOvenTemperatureProgramData = [...ovenTemperatureProgramEntries];

            let matchingOvenTemperatureProgramEntry = editedOvenTemperatureProgramData.find(d => d.ovenTemperatureProgramId === e.row.data.ovenTemperatureProgramId);

            if (matchingOvenTemperatureProgramEntry) {
                //if element was just added but not saved, just get rid of it
                if (matchingOvenTemperatureProgramEntry.editType === EditType.Insert) {
                    const idx = editedOvenTemperatureProgramData.findIndex(d => d.ovenTemperatureProgramId === e.row.data.ovenTemperatureProgramId);

                    if (typeof idx === 'number')
                        editedOvenTemperatureProgramData.splice(idx, 1);
                }
                else {
                    matchingOvenTemperatureProgramEntry.editType = EditType.Delete;
                }

                setOvenTemperatureProgramEntries(editedOvenTemperatureProgramData);
            }
        }
    }

    async function onOvenTemperatureProgramDataValidating(e: any) {
        if (e.oldData && e.oldData.editType !== EditType.Insert)
            e.newData.editType = EditType.Update;
    }

    async function saveOvenTemperatureProgramData() {
        if (!ovenTemperatureProgramEntries)
            return;

        let preparedOvenTemperatureProgramData = [...ovenTemperatureProgramEntries];

        preparedOvenTemperatureProgramData.forEach(d => {
            d.benchSheetId = props.benchSheetId;
        });

        await updateOvenTemperatureProgramData(preparedOvenTemperatureProgramData)
            .then((data) => {
                getOvenTemperaturePrograms();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Temperature Program");
            });
    }

    // CRUD functions

    async function getOvenTemperaturePrograms() {
        await CrudFunctions.getData(`benchsheets/oventemperatureprogramsbybenchSheetid/${props.benchSheetId}`)
            .then(data => {
                setOvenTemperatureProgramEntries(data);
            })

            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Temperature Program");
            });
    }

    //updates

    async function updateOvenTemperatureProgramData(ovenTemperatureProgramData: OvenTemperatureProgram[]) {
        return await CrudFunctions.putData("benchsheets/oventemperatureprogramdata", ovenTemperatureProgramData);
    }

    function onAnalysisDatumInfoButtonClick(e: any) {
        if (ovenTemperatureProgramEntries) {
            let entry = ovenTemperatureProgramEntries.find(pe => pe.ovenTemperatureProgramId === e.key)
            if (entry) {
                let itemBasicInformation: ItemBasicInformation = {
                    rowPointer: entry.ovenTemperatureProgramId,
                    createTimestamp: entry.createTimestamp,
                    lastModifiedTimestamp: entry.modifyTimestamp,
                    createUser: entry.createUser,
                    modifyUser: entry.modifyUser
                };

                setItemBasicInformation(itemBasicInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function editMarkerCellRender(e: any) {
        if (e.data) {
            switch (e.data.editType) {
                case EditType.Insert:
                    return <div><i className="dx-icon-add" /></div>;
                case EditType.Update:
                    return <div><i className="dx-icon-edit" /></div>;
                case EditType.Delete:
                    return <div><i className="dx-icon-trash" /></div>;
                default:
                    return <div></div>;
            }
        }
        return <div></div>;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}